import {
    Box,
    Button,
    Card,
    Chip,
    DialogContent,
    Grid,
    IconButton,
    Modal,
    ModalDialog,
    Stack,
    Typography
} from "@mui/joy";
import React, {useEffect, useState} from "react";
import {ChevronLeftIcon, DownloadIcon, EyeIcon, Library, StarIcon, XIcon} from "lucide-react";
import {
    RESOURCE_TYPE_MAP,
    ResourceType,
    resourceTypeIconMap,
    resourceTypeIconMapBig
} from "../../models/resource";
import {useAsync} from "../../hooks/useAsyncResult";
import {getResource, getResources} from "../../services/api/resource";
import LoadingPageComponent from "../Loading/LoadingPageComponent";
import ResourceLibraryItemComponent from "./ResourceLibraryItemComponent";
import {useSearchParams} from "react-router-dom";
import 'react-mde/lib/styles/css/react-mde-all.css';
import Showdown from "showdown";
import {useUITheme} from "../../hooks/useUITheme";
import {usePagination} from "../../hooks/usePagination";
import PaginationComponent from "../PaginationComponent";
import {useTranslation} from "react-i18next";
import LoadingError from "../Error/LoadingError";

type ResourceLibraryComponentProps = {
    open?: boolean;
    onClose?: () => void;
    resourceInfo?: { [key in ResourceType]?: number }
}

const ResourceLibraryComponent = (props: ResourceLibraryComponentProps) => {
    const uiTheme = useUITheme()
    const [searchParams, setSearchParams] = useSearchParams()

    return <Modal open={props.open}>
        <ModalDialog layout={"fullscreen"} variant="outlined" role="alertdialog"
                     sx={{...uiTheme.backgroundStyle}}>

            {searchParams.get("view") === "library" ? <LibraryView {...props} /> :
                searchParams.get("view") === "resource" ? <ResourceContentView/> : <></>}

        </ModalDialog>
    </Modal>
}

const converter = new Showdown.Converter()


const LibraryView = (props: ResourceLibraryComponentProps) => {
    const uiTheme = useUITheme()
    const [searchParams, setSearchParams] = useSearchParams("?type=all")
    const resourceTypes: (ResourceType | "all")[] = ["all", "audio", "guide", "lesson", "reference", "template", "video"]
    const {t} = useTranslation()
    const resourcesAsyncResult = useAsync(getResources, true, false, true)
    const pageCount = () => {

        const resourceType = searchParams.get("type")
        if (resourceType === "all")
            return Math.ceil(Object.keys(props.resourceInfo || {}).map(resource => props.resourceInfo?.[resource] || 0)
                .reduce((previousValue, currentValue) => previousValue + currentValue, 0) / 15)

        return Math.ceil((props.resourceInfo?.[resourceType] || 0) / 15)
    }

    const loadWithFilters = () => {
        if (searchParams.get("type") === "all")
            resourcesAsyncResult.execute({page: searchParams.get("page") || "1"})
        else
            resourcesAsyncResult.execute({type: searchParams.get("type"), page: searchParams.get("page") || "1"})
    }

    const setFilter = (type: ResourceType | "all") => {
        const urlSearchParams = new URLSearchParams(searchParams)
        urlSearchParams.set("type", type)
        urlSearchParams.delete("page")
        setSearchParams(urlSearchParams)
    }


    const handleClose = () => {
        const url = new URLSearchParams(searchParams)
        url.delete("type")
        url.delete("view")
        url.delete("page")
        setSearchParams(url)
    }

    useEffect(() => {
        if (props.open)
            loadWithFilters()
    }, [searchParams, props.open])

    return <DialogContent sx={{padding: 2, gap: 3}}>
        <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
            <Stack gap={1}>
                <Typography level={"h3"}>
                    {t("resourceLibraryTitle")}
                </Typography>
                <Typography>
                    {t("resourceLibraryDescription")}
                </Typography>
            </Stack>

            <IconButton size={"lg"} onClick={handleClose}>
                <XIcon style={{color: "inherit"}}/>
            </IconButton>

        </Stack>

        <Card variant={"soft"} data-no-glow data-no-animation>

            <Stack direction={"row"} gap={1} flexWrap={"wrap"}>
                {resourceTypes.map(resourceType =>
                    <Chip
                        onClick={searchParams.get("type") === resourceType ? null : () => {
                            const url = new URLSearchParams(searchParams)
                            setFilter(resourceType)
                        }}
                        size={"lg"}
                        startDecorator={resourceTypeIconMap[resourceType] ||
                            <EyeIcon style={{color: "inherit"}}/>} variant={"soft"}
                        sx={{
                            ...(resourceType === searchParams.get("type") && uiTheme.accentStyle),
                            paddingX: 3, borderRadius: 12,
                            paddingY: 1.5, textTransform: "capitalize"
                        }}>
                        {resourceType}
                    </Chip>)}
            </Stack>

        </Card>

        {!resourcesAsyncResult.isSuccess && !resourcesAsyncResult.loading &&
            <LoadingError label={t("resourceLibraryTitle")} onRetry={loadWithFilters}/>}

        {resourcesAsyncResult.loading ?
            <LoadingPageComponent color={"secondary"} label={t("resourceLibraryTitle")}/> :
            resourcesAsyncResult.isSuccess &&
            <Stack>
                <Grid container spacing={3}>
                    {resourcesAsyncResult.payload?.resources.map(value =>
                        <Grid lg={4} sm={6} xs={12}>
                            <ResourceLibraryItemComponent resource={value}/>
                        </Grid>)}
                </Grid>
                <Stack paddingTop={2} alignSelf={"center"}>
                    <PaginationComponent color={"secondary"} pageCount={pageCount()}/>
                </Stack>
            </Stack>}
    </DialogContent>
}


const ResourceContentView = () => {
    const [searchParams, setSearchParams] = useSearchParams()
    const {t} = useTranslation()
    const resourceAsyncResult = useAsync(getResource, true)

    const handleLoadResource = () => {
        resourceAsyncResult.execute(searchParams.get("resourceId"))
    }

    const handleGoBack = () => {
        const urlSearchParams = new URLSearchParams(searchParams)
        urlSearchParams.set("view", "library")
        urlSearchParams.delete("resourceId")
        setSearchParams(urlSearchParams)
    }

    useEffect(() => {
        handleLoadResource()
    }, [])

    return <DialogContent sx={{padding: 2, gap: 3, overflow: "hidden"}}>

        <Button onClick={handleGoBack} color={"neutral"} sx={{width: "max-content"}} variant={"soft"}
                startDecorator={<ChevronLeftIcon/>}>
            {t("backToResourcesActionButtonLabel")}
        </Button>

        {!resourceAsyncResult.isSuccess && !resourceAsyncResult.loading && <LoadingError label={t("resourceLibraryTitle")} onRetry={handleLoadResource} />}

        {resourceAsyncResult.loading ?
            <LoadingPageComponent color={"secondary"} label={t("resourceLabelAlt")}/> :
            resourceAsyncResult.payload &&

            <Card data-no-glow data-no-animation sx={{padding: 4, overflowY: "scroll"}}>
                <Stack gap={2}>
                    <Box data-accent width={"max-content"} display={"flex"}
                         alignItems="center"
                         padding={1} height={"max-content"} sx={{
                        bgcolor: "primary.softBg",
                        color: "primary.softColor"
                    }} borderRadius={10}>
                        {resourceTypeIconMapBig[resourceAsyncResult.payload.type]}
                    </Box>

                    <Stack gap={2} direction={"row"} alignItems={"center"}>
                        <Chip variant={"soft"} size={"lg"}>
                            {resourceAsyncResult.payload.type}
                        </Chip>

                        {resourceAsyncResult.payload.examRelevance?.map(exam =>
                            <Chip variant={"soft"} size={"sm"} color={"secondary"}>
                                {exam}
                            </Chip>)}
                    </Stack>

                    <Stack gap={2} direction={"row"} alignItems={"center"}>
                        <Typography level={"title-md"} startDecorator={<StarIcon fill={"gold"} color={"gold"}/>}>
                            {resourceAsyncResult.payload.rating || "0"} <Typography color={"neutral"}>
                            {t("resourceReviewCountLabel", {reviewCount: resourceAsyncResult.payload.reviewCount})}
                        </Typography>
                        </Typography>

                        <Typography level={"title-md"} startDecorator={<EyeIcon/>}>
                            {resourceAsyncResult.payload.readCount || 0} views
                        </Typography>
                    </Stack>

                    <Card data-no-glow data-no-animation variant={"soft"}>
                        <Box
                            dangerouslySetInnerHTML={{__html: converter.makeHtml(resourceAsyncResult.payload.content)}}
                        />
                    </Card>

                </Stack>

            </Card>}

    </DialogContent>
}


export default ResourceLibraryComponent